import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import FauzyLogic from "@/components/FauzyLogic";
import { logError, addBreadcrumb } from "@/lib/logger";

const ClosedBeta = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim() || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    addBreadcrumb('Submitting waitlist signup', { email }, 'info', 'auth');

    try {
      const { error } = await supabase
        .from("beta_waitlist")
        .insert({ email: email.trim().toLowerCase() });

      if (error) {
        // Check if it's a duplicate email error
        if (error.code === "23505") {
          toast.success("You're already on the waitlist!");
          setSubmitted(true);
        } else {
          throw error;
        }
      } else {
        toast.success("Thanks! We'll notify you when beta access opens.");
        setSubmitted(true);
        addBreadcrumb('Waitlist signup successful', { email }, 'info', 'auth');
      }
    } catch (error) {
      console.error("Failed to join waitlist:", error);
      logError(error instanceof Error ? error : new Error(String(error)), {
        context: "beta_waitlist_signup",
        email,
      });
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Logo */}
        <div className="flex flex-col items-center space-y-4">
          <div className="landing-logo-brand-wrap">
            <div className="landing-logo-brand-inner">
              <img
                src="/logo/light/android-chrome-512.png"
                alt="FauzyLogic Light Logo"
                className="landing-logo-img"
              />
              <h1 className="text-4xl font-bold tracking-tight landing-brand-text">
                <FauzyLogic />
              </h1>
            </div>
          </div>
        </div>

        {/* Closed Beta Message */}
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-semibold">We're in Closed Beta</h2>
          <p className="text-muted-foreground">
            We're currently testing with a limited group of users. Join the waitlist to get early access when we open up.
          </p>
        </div>

        {/* Waitlist Form */}
        {!submitted ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="text-lg"
                autoFocus
                disabled={isSubmitting}
              />
            </div>
            <Button 
              type="submit" 
              size="lg" 
              className="w-full"
              disabled={isSubmitting || !email.trim()}
            >
              {isSubmitting ? "Joining..." : "Join Waitlist"}
            </Button>
          </form>
        ) : (
          <div className="text-center p-6 bg-card border border-border rounded-lg">
            <p className="text-lg font-medium text-foreground">You're on the list!</p>
            <p className="text-sm text-muted-foreground mt-2">
              We'll email you at <span className="font-medium">{email}</span> when we're ready.
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground">
          <a href="https://FauzyLogic.com" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
            Learn more about FauzyLogic
          </a>
        </div>
      </div>
    </div>
  );
};

export default ClosedBeta;
